import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, Modal } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '~/hoc/withLocalization';
import Button from '../../../components/CustomButton/CustomButton.jsx';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';

//utils
import { shortDateFormat, dateToTimeFormat } from '../../../utils/dateFormat';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import { revokeApiKeySVG, generateApiKeySVG } from '../../../utils/constants.js';

class ClientApiSettings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            data: null,
            apiKeyDetails: null,
            showApiKeyModal: false
        }
    }

    componentDidMount() {
        this.loadData();
    }

    async loadData() {
        const { clientStore, t, commonStore, clientId } = this.props;
        try {
            const data = await clientStore.getClientApiKeys(clientId);
            this.setState({ data: data.apiKeys, isLoading: false });
        }
        catch (error) {
            console.error(error);
            commonStore.addNotification(t(error.message || "Something went wrong"), null, 'error');
            this.setState({ isLoading: false });
        }
    }

    handleApiAccessButton(action) {
        this.setState({ isLoading: true });
        switch (action) {
            case 'GRANT_API_ACCESS':
                this.handleSuperAdminActions(true)
                break;
            case 'REVOKE_API_ACCESS':
                this.handleSuperAdminActions(false)
                break;
            default:
                this.handleApiKeyRenewal();
        }
    }



    async handleSuperAdminActions(isAccessGranted) {
        const { t, commonStore, clientId, clientStore } = this.props;
        const title = t("Are you sure");
        const message = t(isAccessGranted ? "You want to give api access to this client? Once granted the keys will be created and shared via mail."
            : "You want to revoke api access of this client? All the existing keys will be revoked and client will no longer have access to API Gateway");
        return confirmAlert({
            title: title,
            message: message,
            buttons: [
                {
                    label: this.props.t('Yes'),
                    onClick: async () => {
                        let toastId = null;
                        try {
                            if (isAccessGranted) {
                                toastId = commonStore.addNotification(t('Please wait, it will take few seconds'), null, 'info', 'top-right', 3000);
                                const response = await clientStore.grantClientApiAccess(clientId);
                                commonStore.dismissToast(toastId);
                                commonStore.addNotification(t('Success'), null, 'success');
                                await this.loadData();
                                this.toggleModal(true, response.data);
                            }
                            else {
                                await clientStore.revokeClientAPIAccess(clientId);
                                commonStore.addNotification(t('Success'), null, 'success');
                                this.loadData();
                            }
                        } catch (error) {
                            if (toastId) commonStore.dismissToast(toastId);
                            commonStore.addNotification(t(error.message || "Something went wrong"), null, 'error');
                            this.setState({ isLoading: false })
                        }
                    },
                },
                {
                    label: this.props.t('No'),
                    onClick: () => this.setState({ isLoading: false })
                },
            ],
        })
    }

    async handleApiKeyRenewal() {
        const { t, commonStore, clientStore } = this.props;
        const title = t("Are you sure");
        const message = t("You want to create new API Key and revoke the existing one? Once created the API key details will be shared via email.");
        return confirmAlert({
            title: title,
            message: message,
            buttons: [
                {
                    label: this.props.t('Yes'),
                    onClick: async () => {
                        const toastId = commonStore.addNotification(t('Please wait, it will take few seconds'), null, 'info', 'top-right', 3000);
                        try {
                            const response = await clientStore.regenerateClientApiKey();
                            commonStore.dismissToast(toastId);
                            commonStore.addNotification(t('Success'), null, 'success');
                            await this.loadData();
                            this.toggleModal(true, response.data);
                        } catch (error) {
                            commonStore.dismissToast(toastId);
                            commonStore.addNotification(t(error.message || "Something went wrong"), null, 'error');
                            this.setState({ isLoading: false })
                        }
                    },
                },
                {
                    label: this.props.t('No'),
                    onClick: () => this.setState({ isLoading: false })
                },
            ],
        })
    }

    toggleModal(isOpen, data) {
        this.setState({ showApiKeyModal: isOpen, apiKeyDetails: data });
    }

    render() {
        const { userStore, t, commonStore } = this.props;
        const { data, isLoading, apiKeyDetails, showApiKeyModal } = this.state;
        if (isLoading) return <LoadingSpinner />
        const userType = userStore.currentUser.user_type;
        const isSuperAdmin = userType === 'super-admin';

        if (!data || !data.length) {
            if (!isSuperAdmin)
                return <Row className='mx-3 mt-2'>
                    <p className='text-center font-size-15 fw-bold'>
                        {t("This service is not enabled, if you require API access or want to know more about it please contact TimeAndControl team")}
                    </p>
                </Row>
            else
                return <Row className='mx-3 mt-5'>
                    <p className='text-center font-size-15 fw-bold'>
                        {t("This service is not enabled for the client, would you like to enable it?")}
                    </p>

                    <Col xs={{ span: 12, offset: 0 }} className='mt-3 text-center'>
                        <Button fill onClick={() => this.handleApiAccessButton('GRANT_API_ACCESS')}>
                            {generateApiKeySVG({ width: 20, fillColor: "#ffffff" })}
                            <span className='ms-1'>
                                {t('Enable API Gateway Access')}
                            </span>
                        </Button>
                    </Col>
                </Row>
        }

        return (
            <>
                <Row className='mx-3 align-items-center'>
                    <Col xs={8}>
                        <p className='text-start m-0'>
                            <span className='font-size-15'>{t('Client ID')}</span>:&nbsp;
                            <span className='font-size-15 fw-bold'>{data[0].client_id}</span>
                        </p>
                    </Col>
                    <Col xs={4} className='text-end'>
                        <Button fill
                            onClick={() => this.handleApiAccessButton(!isSuperAdmin ? 'REGENERATE_API_KEY' : 'REVOKE_API_ACCESS')}>
                            {!isSuperAdmin ? generateApiKeySVG({ width: 20, fillColor: "#ffffff" }) :
                                revokeApiKeySVG({ width: 20, fillColor: "#ffffff" })}
                            <span className='ms-1'>
                                {!isSuperAdmin ? t('Create Api Key') : t('Revoke Api Access')}
                            </span>
                        </Button>
                    </Col>
                </Row>
                <Row className='mx-3'>
                    <table className="table table-hover projectMembersFormTable">
                        <thead>
                            <tr className="table-container-header">
                                <th className='text-start'>{t('Created By')}</th>
                                <th className='text-start'>{t('Created At')}</th>
                                <th className='text-center'>{t('Status')}</th>
                                <th className='text-start'>{t('Revoked By')}</th>
                                <th className='text-start'>{t('Revoked At')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(e => (
                                <tr key={e.createdAt}>
                                    <td className='text-start'>{e.created_by.name}</td>
                                    <td className='text-start'>{e.createdAt ? shortDateFormat(e.createdAt, commonStore.getDateTimeRules()) + " - " + dateToTimeFormat(e.createdAt, commonStore.getDateTimeRules()) : '-'}</td>
                                    <td className={e.status === "ACTIVE" ? 'text-success' : 'text-danger' + " text-center fw-bold"}>
                                        {e.status}
                                    </td>
                                    <td className='text-start'>{e.revoked_by?.name || "-"}</td>
                                    <td className='text-start'>{e.revoked_at ? shortDateFormat(e.revoked_at, commonStore.getDateTimeRules()) + " - " + dateToTimeFormat(e.revoked_at, commonStore.getDateTimeRules()) : "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Row>
                <Modal show={showApiKeyModal}
                    size="md" onHide={() => this.toggleModal(false, null)}>
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center m-0">
                            {t('New API Key')}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            onClick={() => this.toggleModal(false, null)}
                        />
                    </Modal.Header>
                    <Modal.Body>
                        <hr />
                        <Row className='mx-3 mt-2'>
                            <Col xs={4} className='text-start font-size-12 fw-bold'>
                                {t("Client ID")}
                            </Col>
                            <Col xs={8} className='text-start font-size-15 fw-bold text-primary'>
                                {apiKeyDetails?.CLIENT_ID || "-"}
                            </Col>
                        </Row>
                        <hr />
                        <Row className='mx-3 mt-2'>
                            <Col xs={4} className='text-start font-size-12 fw-bold'>
                                {t("API Key")}
                            </Col>
                            <Col xs={8} className='text-start font-size-15 fw-bold text-primary'
                                style={{ wordWrap: "break-word" }}
                            >
                                {apiKeyDetails?.API_KEY || "-"}
                            </Col>
                        </Row>
                        <hr />
                    </Modal.Body>
                </Modal>
            </>
        )
    }
}
export default inject('userStore', 'clientStore', 'commonStore')(withLocalization(observer(ClientApiSettings)));